import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import {
  supabase,
  hasRealSupabaseCreds,
  isDemoModeForced,
  clearDemoOverride,
  isSchemaSetupError,
  supabaseConfig,
} from '../../lib/supabase';
import { useCurrentUser } from '../../lib/currentUser';
import { SETUP_SQL, SETUP_SQL_STATIC_URL, copySetupSql } from '../../lib/setupSql';

// ---------------------------------------------------------------------------
// GoLive page  (route: /go-live)
//
// Audience: JENNIFER, THE TEACHER. Not a developer. Never touched a
// database in her life. She's tried the app, loved the "Get started in
// 3 steps" pattern on the Dashboard, and wants an equivalent for the
// "flip out of practice mode and into a real database" journey. That's
// this page.
//
// What it does:
//
//  1. Auto-detects where she is on the journey:
//     - hasCreds  — VITE_SUPABASE_URL + KEY are baked into this build
//     - hasSchema — a `select 1 from students` query didn't hit a
//                   missing-table / missing-column error
//     - hasUser   — there's a real Supabase auth session (not the
//                   demo localStorage-only one)
//     All three green ⇒ she's LIVE. Big green hero banner + "You did
//     it" copy. Otherwise: amber "you're X of 3 done" banner + the
//     unfinished steps stay actionable.
//
//  2. Gives her the two things she actually needs to click:
//     - **Copy setup SQL** — one tap puts the entire
//       `APPLY_ALL_MIGRATIONS.sql` on her clipboard, so pasting into
//       Supabase's SQL Editor is a straight paste + Run.
//     - **Deep-links into her own Supabase project** — SQL Editor,
//       Auth → Users, URL Configuration. Only rendered when we can
//       derive the project ref from `VITE_SUPABASE_URL`; otherwise
//       we fall back to a generic "open Supabase dashboard" link.
//
//  3. Explains the third thing she can't do without her IT person /
//     hosting admin: setting env vars on Cloudflare Pages + a redeploy.
//     Copy buttons for the two variable names so nobody misspells them.
//
//  4. Two "outs" she can email/share with her IT coordinator:
//     - Copy this page's URL
//     - Link to GO_LIVE.md on GitHub
//
// The visual language mirrors the Dashboard's `GetStartedGuide`
// component (blue → green stepper) so this feels like the natural
// continuation of the same journey.
// ---------------------------------------------------------------------------

const REPO_URL = 'https://github.com/Senergy-hub/edutracker_pro';
const SUPABASE_SETUP_SQL_URL = SETUP_SQL_STATIC_URL;
const GO_LIVE_DOC_URL = `${REPO_URL}/blob/main/GO_LIVE.md`;
const KEEPALIVE_DOC_URL = `${REPO_URL}/blob/main/docs/ADMIN_GUIDE.md#5-github-actions--supabase-keep-alive`;

// Parse `https://abcdefg.supabase.co` → `abcdefg`. Any other shape
// (custom domain, empty, malformed) returns null and the caller falls
// back to a generic dashboard link.
function projectRefFromUrl(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const parsed = new URL(url);
    const [ref] = parsed.hostname.split('.');
    if (!ref || ref === 'your-project' || ref === 'dummy') return null;
    return ref;
  } catch {
    return null;
  }
}

function supabaseDeepLink(projectRef, subpath) {
  if (!projectRef) return 'https://supabase.com/dashboard';
  return `https://supabase.com/dashboard/project/${projectRef}${subpath}`;
}

// Tiny state helper for a button that becomes "Copied ✓" for 3s after
// clicking. Used a few times on this page so it's centralized.
function useCopyState() {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 3000);
    return () => clearTimeout(t);
  }, [copied]);
  return [copied, setCopied];
}

async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Runs a cheap "does this DB have our tables?" probe once on mount and
// after the user hits "Re-check." Returns { status, error } where
// status is one of 'idle' | 'checking' | 'ok' | 'missing_schema' |
// 'no_client' | 'error'.
function useSchemaProbe(hasCreds) {
  const [state, setState] = useState({ status: hasCreds ? 'checking' : 'no_client', error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!hasCreds || !supabase) {
      setState({ status: 'no_client', error: null });
      return () => {};
    }
    setState((s) => ({ ...s, status: 'checking' }));

    (async () => {
      try {
        // limit(0) is enough — we only care whether the query resolves
        // or trips a schema error. head:true skips the row transfer.
        const { error } = await supabase
          .from('students')
          .select('id', { count: 'exact', head: true })
          .limit(0);
        if (cancelled) return;
        if (!error) {
          setState({ status: 'ok', error: null });
          return;
        }
        if (isSchemaSetupError(error)) {
          setState({ status: 'missing_schema', error });
          return;
        }
        setState({ status: 'error', error });
      } catch (err) {
        if (cancelled) return;
        setState({ status: 'error', error: err });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasCreds, tick]);

  return [state, () => setTick((n) => n + 1)];
}

function StepShell({ n, title, done, active, children }) {
  return (
    <li
      className={`flex items-start gap-4 rounded-xl border p-5 sm:p-6 ${
        done
          ? 'border-green-200 bg-green-50/60'
          : active
          ? 'border-blue-200 bg-white shadow-sm'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-semibold text-base ${
          done ? 'bg-green-500 text-white' : 'bg-blue-100 text-blue-700'
        }`}
        aria-hidden="true"
      >
        {done ? <Icon name="Check" size={18} /> : n}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </li>
  );
}

const GoLive = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const currentUser = useCurrentUser();

  const hasCreds = hasRealSupabaseCreds();
  const forcedDemo = isDemoModeForced();
  const [schema, recheck] = useSchemaProbe(hasCreds);
  const projectRef = useMemo(
    () => projectRefFromUrl(supabaseConfig.url),
    [],
  );

  const hasSchema = schema.status === 'ok';
  const hasSignedInUser = currentUser?.backend === 'supabase';
  const stepsDone = [hasCreds, hasSchema, hasSignedInUser].filter(Boolean).length;
  const isLive = hasCreds && hasSchema && hasSignedInUser && !forcedDemo;

  // Demo-mode lockdown.
  //
  // Any visitor who is NOT a real Supabase-authed user (i.e. anyone
  // signed in via a "Try demo — <role> view" button, or anyone who
  // just hasn't signed in yet) must NOT see the /go-live page's
  // contents. Prior to this gate, that page leaked:
  //
  //   * The owner's real Supabase project URL and ref
  //   * Copy buttons for VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY
  //   * The entire step-by-step "spin up your own instance" playbook
  //
  // Which is exactly the "hard to make people pay for a program they
  // can get free" trap the customer flagged.
  //
  // The gate is intentionally broader than "forcedDemo" — a demo
  // visitor who closes and reopens the tab loses the sessionStorage
  // demo flag while keeping the localStorage demo session, so
  // `isDemoModeForced()` returns false while `hasSignedInUser` is
  // still false. Guarding on hasSignedInUser alone catches every
  // non-owner path.
  //
  // The one legitimate case the gate leaves through: `!hasCreds`,
  // where there IS no real Supabase project yet. In that state the
  // page is a self-serve setup flow for the owner and there's no
  // secret to leak (nothing's configured), so we let it render.
  if (hasCreds && !hasSignedInUser) {
    return (
      <DemoLockedPage
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuToggle={() => setIsSidebarOpen((v) => !v)} isMenuOpen={isSidebarOpen} />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((v) => !v)}
      />

      <main
        className={`transition-all duration-300 pt-16 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <Breadcrumb />

          <PageHeader />

          <HeroBanner
            stepsDone={stepsDone}
            isLive={isLive}
            forcedDemo={forcedDemo}
            hasCreds={hasCreds}
          />

          <ol className="space-y-4 mt-6">
            <StepOne
              hasCreds={hasCreds}
              stepsDone={stepsDone}
              supabaseUrl={supabaseConfig.url}
              projectRef={projectRef}
            />
            <StepTwo
              hasCreds={hasCreds}
              hasSchema={hasSchema}
              schemaStatus={schema.status}
              projectRef={projectRef}
              onRecheck={recheck}
            />
            <StepThree
              hasCreds={hasCreds}
              hasSchema={hasSchema}
              hasSignedInUser={hasSignedInUser}
              currentUser={currentUser}
              projectRef={projectRef}
            />
          </ol>

          <Extras projectRef={projectRef} forcedDemo={forcedDemo} />
        </div>
      </main>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PageHeader = () => (
  <div className="mb-6">
    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
      Go live in 3 steps
    </h1>
    <p className="mt-2 text-base sm:text-lg text-gray-600 max-w-2xl">
      Right now anything you type is in a safe practice space. When you're
      ready to start saving real student data, follow the steps below.
      Each one turns green when it's done.
    </p>
  </div>
);

const HeroBanner = ({ stepsDone, isLive, forcedDemo, hasCreds }) => {
  if (isLive) {
    return (
      <div className="rounded-xl border border-green-300 bg-green-50 p-5 sm:p-6 flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
          <Icon name="Check" size={24} className="text-white" />
        </div>
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-semibold text-green-900">
            You're live!
          </h2>
          <p className="mt-1 text-green-800 text-base">
            Your real database is on. Everything you type from here saves
            to Supabase and shows up on every device you sign into.
          </p>
        </div>
      </div>
    );
  }

  if (forcedDemo && hasCreds) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center">
            <Icon name="Eye" size={22} className="text-amber-900" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-semibold text-amber-900">
              You're viewing the demo on a live site
            </h2>
            <p className="mt-1 text-amber-900/90">
              Your app IS connected to a real database, but this browser
              tab has demo mode turned on (a{' '}
              <code className="font-mono text-sm">?demo=1</code> flag
              in the URL). Anything you type here is saved to this
              browser only — nothing goes to your real database.
            </p>
            <div className="mt-3">
              <Button
                variant="warning"
                size="lg"
                iconName="LogOut"
                iconPosition="left"
                onClick={() => {
                  clearDemoOverride();
                  if (typeof window !== 'undefined') {
                    const url = new URL(window.location.href);
                    url.searchParams.delete('demo');
                    url.searchParams.delete('mode');
                    url.searchParams.set('live', '1');
                    window.location.href = url.toString();
                  }
                }}
              >
                Exit demo mode
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
          <Icon name="Sparkles" size={22} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-semibold text-gray-900">
            {stepsDone === 0
              ? "You're in practice mode"
              : `You're ${stepsDone} of 3 steps done`}
          </h2>
          <p className="mt-1 text-gray-700">
            {stepsDone === 0
              ? 'Nothing you type is saved to real records. Complete the 3 steps below and you\'ll be storing real data.'
              : "Nice work. Finish the last step(s) below to fully switch to your real database."}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <div className="h-2 bg-blue-100 rounded-full flex-1 overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${(stepsDone / 3) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-blue-800">
              {stepsDone}/3
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 1: Create a Supabase project + copy credentials
// ---------------------------------------------------------------------------
const StepOne = ({ hasCreds, stepsDone, supabaseUrl, projectRef }) => {
  const [copiedUrl, setCopiedUrl] = useCopyState();
  const [copiedKey, setCopiedKey] = useCopyState();

  return (
    <StepShell
      n={1}
      title="Create a Supabase project and connect it"
      done={hasCreds}
      active={!hasCreds && stepsDone === 0}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-gray-900">
          Create a Supabase project and connect it
        </h3>
        {hasCreds && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
            <Icon name="Check" size={12} />
            Done
          </span>
        )}
      </div>
      <p className="mt-2 text-gray-700">
        Supabase is a free service that stores your students, goals, and
        incident logs online. This is a one-time step you (or your school's
        tech coordinator) does. It takes about 5 minutes.
      </p>

      {!hasCreds && (
        <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-3">
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-800">
            <li>
              Go to{' '}
              <a
                href="https://supabase.com/dashboard"
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2 text-blue-700 hover:text-blue-900"
              >
                supabase.com/dashboard
              </a>{' '}
              and sign in (free account is fine).
            </li>
            <li>
              Click <strong>New Project</strong>. Give it a name, pick a
              region close to your school, and set a strong password.
              Save the password somewhere safe. Wait ~2 minutes for it to
              finish creating.
            </li>
            <li>
              Once it's ready, click <strong>Project Settings</strong> in
              the left sidebar, then <strong>API</strong>. You'll see two
              values you need to copy: <em>Project URL</em> and{' '}
              <em>anon</em> <em>public</em> key. (Ignore the{' '}
              <em>service_role</em> secret key — that one should never
              leave Supabase.)
            </li>
            <li>
              Give those two values to whoever runs the website
              (Cloudflare Pages or Netlify). See Step 3 for the exact
              variable names and where they go.
            </li>
          </ol>

          <div className="flex flex-wrap gap-2">
            <a
              href="https://supabase.com/dashboard"
              target="_blank"
              rel="noreferrer"
              className="inline-block"
            >
              <Button variant="default" size="lg" iconName="ExternalLink" iconPosition="right">
                Open Supabase
              </Button>
            </a>
          </div>
        </div>
      )}

      {hasCreds && (
        <div className="mt-4 rounded-lg bg-white border border-green-200 p-4 space-y-2 text-sm">
          <p className="text-gray-800">
            Great — the app already has your Supabase credentials.
          </p>
          <dl className="space-y-1">
            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
              <dt className="font-medium text-gray-700 min-w-[120px]">Project URL:</dt>
              <dd className="font-mono text-xs text-gray-800 break-all">
                {supabaseUrl}
              </dd>
            </div>
            {projectRef && (
              <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                <dt className="font-medium text-gray-700 min-w-[120px]">Project ref:</dt>
                <dd className="font-mono text-xs text-gray-800">{projectRef}</dd>
              </div>
            )}
          </dl>
          {projectRef && (
            <div className="pt-1">
              <a
                href={supabaseDeepLink(projectRef, '')}
                target="_blank"
                rel="noreferrer"
              >
                <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
                  Open my Supabase project
                </Button>
              </a>
            </div>
          )}
        </div>
      )}

      {/* Copy-name helpers — useful whether creds are set or not, so
          Jennifer's IT person always sees the exact variable names. */}
      <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50/40 p-4">
        <p className="text-sm font-medium text-gray-800 mb-2">
          The two environment variables (copy these names for your
          hosting settings — Step 3):
        </p>
        <div className="grid sm:grid-cols-2 gap-2">
          <div className="flex items-center justify-between rounded-md bg-white border border-gray-200 px-3 py-2 text-sm">
            <code className="font-mono text-gray-800">VITE_SUPABASE_URL</code>
            <button
              type="button"
              onClick={async () => {
                if (await copyToClipboard('VITE_SUPABASE_URL')) setCopiedUrl(true);
              }}
              className="ml-2 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              <Icon name={copiedUrl ? 'Check' : 'Copy'} size={12} />
              {copiedUrl ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="flex items-center justify-between rounded-md bg-white border border-gray-200 px-3 py-2 text-sm">
            <code className="font-mono text-gray-800">VITE_SUPABASE_ANON_KEY</code>
            <button
              type="button"
              onClick={async () => {
                if (await copyToClipboard('VITE_SUPABASE_ANON_KEY')) setCopiedKey(true);
              }}
              className="ml-2 inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
            >
              <Icon name={copiedKey ? 'Check' : 'Copy'} size={12} />
              {copiedKey ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </StepShell>
  );
};

// ---------------------------------------------------------------------------
// Step 2: Install the database schema by pasting one SQL file
// ---------------------------------------------------------------------------
const StepTwo = ({ hasCreds, hasSchema, schemaStatus, projectRef, onRecheck }) => {
  const [copied, setCopied] = useCopyState();
  const [fetchError, setFetchError] = useState(null);
  const [showManual, setShowManual] = useState(false);

  const isActive = hasCreds && !hasSchema;

  // The copy handler MUST stay synchronous: iOS Safari revokes clipboard
  // permission the moment we await a fetch inside the tap handler. The
  // SETUP_SQL constant is bundled at build time via `?raw` (see
  // src/lib/setupSql.js), so the text is already sitting in memory by
  // the time this component renders and copySetupSql() can call
  // navigator.clipboard.writeText in the same task as the tap.
  function handleCopySql(event) {
    setFetchError(null);
    try {
      event?.preventDefault?.();
    } catch {}
    copySetupSql()
      .then((ok) => {
        if (ok) {
          setCopied(true);
        } else {
          setFetchError(
            "Your browser wouldn't let us copy automatically — this happens on iOS sometimes. Use the manual copy area below (it works every time).",
          );
          setShowManual(true);
        }
      })
      .catch((err) => {
        setFetchError(
          `Could not copy (${err?.message || err}). Use the manual copy area below.`,
        );
        setShowManual(true);
      });
  }

  return (
    <StepShell n={2} title="Install the database" done={hasSchema} active={isActive}>
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-gray-900">
          Install the database
        </h3>
        {hasSchema && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
            <Icon name="Check" size={12} />
            Done
          </span>
        )}
        {!hasCreds && (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            Finish Step 1 first
          </span>
        )}
      </div>
      <p className="mt-2 text-gray-700">
        This is one paste of SQL into your Supabase project. It creates
        every table the app expects. Safe to re-run if something didn't
        stick the first time — nothing is overwritten.
      </p>

      {!hasSchema && (
        <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-3">
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-800">
            <li>
              Click <strong>Copy setup SQL</strong> below. That copies a
              single (long) SQL file to your clipboard.
            </li>
            <li>
              Click <strong>Open SQL Editor</strong> to jump straight to
              your Supabase project's SQL Editor. Sign in if it asks.
            </li>
            <li>
              Click the green <strong>+ New query</strong> button, paste
              (Ctrl+V or ⌘V), and click <strong>Run</strong> (or press
              Ctrl+Enter).
            </li>
            <li>
              Come back to this tab and click <strong>Re-check</strong>{' '}
              below. The step should flip green.
            </li>
          </ol>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="default"
              size="lg"
              iconName={copied ? 'Check' : 'Copy'}
              iconPosition="left"
              onClick={handleCopySql}
              disabled={!hasCreds}
            >
              {copied ? 'Copied — now paste into Supabase' : 'Copy setup SQL'}
            </Button>
            <a
              href={supabaseDeepLink(projectRef, '/sql/new')}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant="outline"
                size="lg"
                iconName="ExternalLink"
                iconPosition="right"
                disabled={!hasCreds}
              >
                Open SQL Editor
              </Button>
            </a>
            {/* Direct-link download. Using a plain <a download> instead
                of a JS click handler is more reliable on mobile Safari,
                which sometimes swallows programmatic downloads that
                depend on Blob URLs. */}
            <a
              href={SUPABASE_SETUP_SQL_URL}
              download="EduTrackerPro-database-setup.sql"
            >
              <Button
                variant="ghost"
                size="lg"
                iconName="Download"
                iconPosition="left"
              >
                Download .sql file
              </Button>
            </a>

            <Button
              variant="ghost"
              size="lg"
              iconName={showManual ? 'ChevronUp' : 'ChevronDown'}
              iconPosition="right"
              onClick={() => setShowManual((v) => !v)}
            >
              {showManual ? 'Hide manual copy' : 'Show SQL to copy manually'}
            </Button>
          </div>

          {fetchError && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              {fetchError}{' '}
              <a
                href={`${REPO_URL}/blob/main/supabase/APPLY_ALL_MIGRATIONS.sql`}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                Open the file on GitHub
              </a>
              .
            </div>
          )}

          {/* Manual-copy fallback: a visible, always-selectable text area
              containing the entire setup SQL. This is the escape hatch
              for cases where clipboard permission is silently denied
              (iOS Safari, Firefox private mode, some in-app browsers) —
              the teacher can long-press → Select All → Copy right from
              the page, no downloads or share sheets needed. Rendered as
              a real <textarea readonly> because it's the most reliable
              cross-browser way to get Select All + Copy without any
              hoops. */}
          {showManual && (
            <div className="mt-3 rounded-md border border-gray-300 bg-white p-3">
              <p className="text-sm font-semibold text-gray-900">
                Manual copy (works even when the button doesn't)
              </p>
              <ol className="mt-1 mb-2 list-decimal list-inside space-y-0.5 text-xs text-gray-700">
                <li>Tap inside the box below.</li>
                <li>
                  On iPhone / iPad: press &amp; hold anywhere in the text →
                  tap <strong>Select All</strong> → tap <strong>Copy</strong>.
                </li>
                <li>
                  On a computer: press <strong>Ctrl+A</strong> (or ⌘+A) to
                  select all, then <strong>Ctrl+C</strong> (or ⌘+C) to copy.
                </li>
                <li>
                  Open the SQL Editor above, paste, and click{' '}
                  <strong>Run</strong>.
                </li>
              </ol>
              <textarea
                readOnly
                value={SETUP_SQL}
                className="w-full h-56 rounded border border-gray-300 bg-gray-50 p-2 font-mono text-[11px] text-gray-800"
                onClick={(e) => {
                  // Auto-select-all on tap so mobile users can go
                  // straight to the OS Copy affordance.
                  try {
                    e.currentTarget.focus();
                    e.currentTarget.setSelectionRange(0, SETUP_SQL.length);
                  } catch {}
                }}
              />
              <p className="mt-1 text-[11px] text-gray-500">
                About {Math.round((SETUP_SQL?.length || 0) / 1024)} KB of SQL.
                All statements are safe to re-run.
              </p>
            </div>
          )}
        </div>
      )}

      {hasCreds && (
        <div className="mt-3 flex items-center gap-3 text-sm text-gray-600">
          <Button
            variant="ghost"
            size="sm"
            iconName={schemaStatus === 'checking' ? 'Loader' : 'RefreshCcw'}
            iconPosition="left"
            onClick={onRecheck}
            loading={schemaStatus === 'checking'}
          >
            Re-check database
          </Button>
          <span className="text-xs">
            {schemaStatus === 'checking' && 'Checking…'}
            {schemaStatus === 'ok' && (
              <span className="text-green-700 font-medium">
                ✓ Database is ready
              </span>
            )}
            {schemaStatus === 'missing_schema' && (
              <span className="text-amber-800">
                Not ready yet — run the SQL above and come back.
              </span>
            )}
            {schemaStatus === 'error' && (
              <span className="text-red-700">
                Couldn't reach your database. Check the URL / key in Step 1.
              </span>
            )}
          </span>
        </div>
      )}
    </StepShell>
  );
};

// ---------------------------------------------------------------------------
// Step 3: Create your first sign-in
// ---------------------------------------------------------------------------
const StepThree = ({ hasCreds, hasSchema, hasSignedInUser, currentUser, projectRef }) => {
  const isActive = hasCreds && hasSchema && !hasSignedInUser;
  const isBlocked = !hasCreds || !hasSchema;

  return (
    <StepShell
      n={3}
      title="Create your first sign-in"
      done={hasSignedInUser}
      active={isActive}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-lg font-semibold text-gray-900">
          Create your first sign-in
        </h3>
        {hasSignedInUser && (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
            <Icon name="Check" size={12} />
            Signed in as {currentUser?.email || 'you'}
          </span>
        )}
        {isBlocked && (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            Finish {!hasCreds ? 'Step 1' : 'Step 2'} first
          </span>
        )}
      </div>
      <p className="mt-2 text-gray-700">
        Add yourself (and any teammates) as users in Supabase. Then sign
        in on the Login page like you would any other website.
      </p>

      {!hasSignedInUser && (
        <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-4 space-y-3">
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-800">
            <li>
              Open Supabase → <strong>Authentication → Users</strong>.
            </li>
            <li>
              Click <strong>Add user → Create new user</strong>. Use your
              real work email. Set a temporary password.
            </li>
            <li>
              Come to the app's <strong>Sign In</strong> page and sign in
              with that email + temp password.
            </li>
            <li>
              Change your password from the profile menu once you're in.
            </li>
          </ol>

          <div className="flex flex-wrap gap-2">
            <a
              href={supabaseDeepLink(projectRef, '/auth/users')}
              target="_blank"
              rel="noreferrer"
            >
              <Button
                variant="default"
                size="lg"
                iconName="ExternalLink"
                iconPosition="right"
                disabled={isBlocked}
              >
                Open Supabase → Users
              </Button>
            </a>
            <a href="/login">
              <Button
                variant="outline"
                size="lg"
                iconName="LogIn"
                iconPosition="left"
                disabled={isBlocked}
              >
                Go to Sign In
              </Button>
            </a>
          </div>
        </div>
      )}
    </StepShell>
  );
};

// ---------------------------------------------------------------------------
// Extras — collapsible, low-priority items below the fold
// ---------------------------------------------------------------------------
const Extras = ({ projectRef, forcedDemo }) => {
  const [open, setOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useCopyState();
  const pageUrl =
    typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';

  return (
    <div className="mt-8 border-t border-gray-200 pt-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        aria-expanded={open}
      >
        <Icon name={open ? 'ChevronDown' : 'ChevronRight'} size={16} />
        A few more one-time things (optional, but recommended)
      </button>

      {open && (
        <div className="mt-4 space-y-4 text-sm text-gray-700">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="font-semibold text-gray-900 mb-1">
              Send this page to your IT person
            </h4>
            <p className="mb-2">
              If someone else is handling the Supabase and Cloudflare
              parts, forward them one of these:
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                iconName={copiedLink ? 'Check' : 'Link'}
                iconPosition="left"
                onClick={async () => {
                  if (await copyToClipboard(pageUrl)) setCopiedLink(true);
                }}
              >
                {copiedLink ? 'Link copied' : 'Copy link to this page'}
              </Button>
              <a href={GO_LIVE_DOC_URL} target="_blank" rel="noreferrer">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="right"
                >
                  Open the plain-English go-live guide
                </Button>
              </a>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="font-semibold text-gray-900 mb-1">
              Allow the &ldquo;Forgot Password&rdquo; email
            </h4>
            <p>
              So teachers can reset their own password without help,
              open Supabase → <strong>Authentication → URL Configuration</strong>{' '}
              and add your production URL plus{' '}
              <code className="font-mono text-xs">
                https://your-domain/reset-password
              </code>{' '}
              to the Redirect URLs list.
            </p>
            <div className="mt-2">
              <a
                href={supabaseDeepLink(projectRef, '/auth/url-configuration')}
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="right"
                >
                  Open Supabase → URL Configuration
                </Button>
              </a>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h4 className="font-semibold text-gray-900 mb-1">
              Keep the free-tier database from &ldquo;pausing&rdquo;
            </h4>
            <p>
              Supabase auto-pauses a free-tier project after 7 days of no
              activity. This repo ships a GitHub Actions workflow that
              pings the DB every 3 days so it stays awake. Turn it on
              once and forget it exists.
            </p>
            <div className="mt-2">
              <a href={KEEPALIVE_DOC_URL} target="_blank" rel="noreferrer">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="right"
                >
                  How to turn on keep-alive
                </Button>
              </a>
            </div>
          </div>

          {!forcedDemo && (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900 mb-1">
                Show a prospect the app without touching real data
              </h4>
              <p>
                Add{' '}
                <code className="font-mono text-xs">?demo=1</code> to the
                end of any page's URL. That flag flips just this browser
                tab into demo mode. Perfect for sales walk-throughs.
                Close the tab (or add <code className="font-mono text-xs">?live=1</code>) to switch back.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DemoLockedPage
//
// Rendered instead of the real /go-live page when a forced-demo visitor
// tries to reach it. Deliberately says nothing about Supabase, project
// URLs, migrations, or environment variables — the whole point of this
// gate is that setup mechanics are a paid-plan artifact, not a demo
// giveaway. Two exits: back to the demo, or sign up / sign in.
// ---------------------------------------------------------------------------
const DemoLockedPage = ({
  isSidebarOpen,
  setIsSidebarOpen,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
}) => {
  const handleGoBack = () => {
    if (typeof window !== 'undefined') {
      window.history.length > 1
        ? window.history.back()
        : (window.location.href = '/dashboard');
    }
  };

  const handleExitDemoAndSignUp = () => {
    // Same pattern DemoModeBanner uses: strip the demo session and
    // route to the login page (which is the sign-up entry point too).
    clearDemoOverride();
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem('userSession');
      } catch {
        // Non-fatal — the redirect below still pulls the visitor out.
      }
      window.location.href = `${window.location.origin}/login?live=1`;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen((v) => !v)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((v) => !v)}
      />
      <main
        className={`transition-all duration-300 pt-16 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        }`}
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 text-primary mb-6">
            <Icon name="Lock" size={36} />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-3">
            Setup is a paid-plan feature.
          </h1>
          <p className="text-lg text-muted-foreground mb-8 max-w-lg mx-auto">
            You're exploring the demo, so setup steps aren't shown here.
            Setup gets your school its own private database and its own
            teacher accounts — that comes with your subscription.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
            <Button
              size="lg"
              onClick={handleExitDemoAndSignUp}
              fullWidth
              className="sm:w-auto"
              iconName="LogIn"
              iconPosition="left"
            >
              Sign up or sign in
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleGoBack}
              fullWidth
              className="sm:w-auto"
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back to the demo
            </Button>
          </div>
          <p className="mt-8 text-xs text-muted-foreground max-w-md mx-auto">
            Already a customer? Sign in with your account and this page
            will show you the setup checklist automatically.
          </p>
        </div>
      </main>
    </div>
  );
};

export default GoLive;
